import { computed } from 'vue'
import { TinyColor } from '@ctrl/tinycolor'
import { useDisabled, useNamespace } from '@element-plus/hooks'
import type { ButtonProps } from './button'

export function darken(color: TinyColor, amount = 20) {
  return color.mix('#fff', amount).toString()
}

export function useButtonCustomStyle(props: ButtonProps) {
  const _disabled = useDisabled()
  const ns = useNamespace('button')

  // calculate hover & active color by custom color
  // only work when custom color
  return computed(() => {
    let styles: Record<string, string> = {}

    const buttonColor = props.color

    if (buttonColor) {
      const color = new TinyColor(buttonColor)
      const activeBgColor = props.dark
        ? color.tint(20).toString()
        : darken(color, 20)

      if (props.plain) {
        styles = ns.cssVarBlock({
          'bg-color': props.dark
            ? darken(color, 90)
            : color.tint(90).toString(),
          'text-color': buttonColor,
          'border-color': props.dark
            ? darken(color, 50)
            : color.tint(50).toString(),
          'hover-text-color': `var(${ns.cssVarName('color-white')})`,
          'hover-bg-color': buttonColor,
          'hover-border-color': buttonColor,
          'active-bg-color': activeBgColor,
          'active-text-color': `var(${ns.cssVarName('color-white')})`,
          'active-border-color': activeBgColor,
        })

        if (_disabled.value) {
          styles[ns.cssVarBlockName('disabled-bg-color')] = props.dark
            ? darken(color, 90)
            : color.tint(90).toString()
          styles[ns.cssVarBlockName('disabled-text-color')] = props.dark
            ? darken(color, 50)
            : color.tint(50).toString()
          styles[ns.cssVarBlockName('disabled-border-color')] = props.dark
            ? darken(color, 50)
            : color.tint(50).toString()
        }
      } else if (props.border) {
        const varMap = {
          'bg-color': `var(${ns.cssVarName('color-white')})`,
          'text-color': buttonColor,
          'border-color': buttonColor,
          'hover-text-color': buttonColor,
          'hover-bg-color': darken(color, 90),
          'hover-border-color': buttonColor,
          'active-text-color': `var(${ns.cssVarName('color-white')})`,
          'active-bg-color': buttonColor,
          'active-border-color': buttonColor,
          'disabled-bg-color': `var(${ns.cssVarName('color-white')})`,
          'disabled-border-color': darken(color, 50),
          'disabled-text-color': darken(color, 50),
        }
        // eslint-disable-next-line array-callback-return
        Object.keys(varMap).map((e) => {
          const key = e as keyof typeof varMap
          styles[ns.cssVarBlockName(e)] = varMap[key]
        })
        // styles = {
        //   '--el-button-bg-color': 'var(--el-color-white)',
        //   '--el-button-text-color': buttonColor,
        //   '--el-button-border-color': buttonColor,
        //   '--el-button-hover-text-color': buttonColor,
        //   '--el-button-hover-bg-color': darken(color, 90),
        //   '--el-button-hover-border-color': buttonColor,
        //   '--el-button-active-text-color': 'var(--el-color-white)',
        //   '--el-button-active-bg-color': buttonColor,
        //   '--el-button-active-border-color': buttonColor,
        //   '--el-button-disabled-bg-color': 'var(--el-color-white)',
        //   '--el-button-disabled-border-color': darken(color, 50),
        //   '--el-button-disabled-text-color': darken(color, 50),
        // }
      } else {
        const hoverBgColor = props.dark
          ? darken(color, 30)
          : color.tint(20).toString()
        const textColor = color.isDark()
          ? `var(${ns.cssVarName('color-white')})`
          : `var(${ns.cssVarName('color-black')})`
        styles = ns.cssVarBlock({
          'bg-color': buttonColor,
          'text-color': textColor,
          'border-color': buttonColor,
          'hover-bg-color': hoverBgColor,
          'hover-text-color': textColor,
          'hover-border-color': hoverBgColor,
          'active-bg-color': activeBgColor,
          'active-border-color': activeBgColor,
        })

        if (_disabled.value) {
          const disabledButtonColor = props.dark
            ? darken(color, 50)
            : color.tint(50).toString()
          styles[ns.cssVarBlockName('disabled-bg-color')] = disabledButtonColor
          styles[ns.cssVarBlockName('disabled-text-color')] = props.dark
            ? 'rgba(255, 255, 255, 0.5)'
            : `var(${ns.cssVarName('color-white')})`
          styles[ns.cssVarBlockName('disabled-border-color')] =
            disabledButtonColor
        }
      }
    }

    return styles
  })
}
